/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';

// Component imports
import { Button, Card, Col, Divider, Empty, Form, Input, Row, Select, Spin, Typography, Collapse, Checkbox, Switch, Dropdown, Tooltip, Modal } from 'antd';
import { QuestionCircleOutlined, CopyOutlined, MinusCircleOutlined } from '@ant-design/icons';
import IntegrationForm from './IntegrationForm';
import LoginScreen from '../../screens/LoginScreen';
import SideModal from '../Layout/SideModal';
import FormCard from './FormCard';
import { PlusIcon } from '../Icons/PlusIcon';
import { ArrowDownIcon } from '../Icons/ArrowDownIcon';

// Misc imports
import { PERMISSIONS_ORG, PERMISSIONS_GROUP_ADMIN } from '../../constants/main';
import useUserPermissions from '../../utils/hooks/useUserPermissions';
import { copyTextToClipboard } from '../../utils/function/copyToClipboard';
import { useFetchOrganizationQuery, useUpdateOrganizationMutation } from '../../store/organization/organizationApi';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useFetchIntegrationsQuery } from '../../store/integrations/integrationsApi';
import useTheme from '../../utils/hooks/useTheme';
import { INTEGRATION_CATEGORY, INTEGRATION_TYPE } from '../../constants/integrations';

const OrganizationLoginSettingsForm = ({ setHasPendingChanges, isUpdateMode = false, setFormRef }) => {
  const [form] = Form.useForm();
  const { orgId } = useParams();
  const { t } = useTranslation();
  const { formProps } = useTheme();
  const userPermissions = useUserPermissions(orgId, null);
  const { data: organization, isFetching: isLoadingOrganization, isError: fetchOrganizationError } = useFetchOrganizationQuery({ orgId, urlParams: { includeLoginSettings: true } }, { skip: !orgId });
  const { data: orgIntegrations, isFetching: isLoadingOrgIntegrations } = useFetchIntegrationsQuery({ orgId, urlParams: { pageSize: 1000, type: INTEGRATION_TYPE.OIDC_AUTHENTICATION } }, { skip: !orgId });
  const [updateOrganization, { isLoading: isUpdatingOrganization }] = useUpdateOrganizationMutation();
  const { isLoading: isFetchingMediaFiles } = getCurrentQueryState('fetchMediaFiles', { orgId });

  const [selectedIntegration, setSelectedIntegration] = useState();
  const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);

  const [headers, _setHeaders] = useState([{ key: '', value: '' }]);
  const [roles, _setRoles] = useState([{ key: '', value: '' }]);
  const [pluginRefs, _setPluginRefs] = useState([]);
  const [isSkipNativeLoginEnabled, setIsSkipNativeLoginEnabled] = useState(organization?.loginSettings?.isSkipNativeLoginEnabled);
  const [, updateState] = useState(); // Hook to force a component re-render

  const hasEditPermissions = useMemo(() => {
    return userPermissions.some(permission => PERMISSIONS_GROUP_ADMIN.includes(permission));
  }, [userPermissions]);

  const _setHasPendingChanges = useCallback((value) => {
    if (hasEditPermissions) {
      setHasPendingChanges?.(value);
    }
  }, [setHasPendingChanges]);

  const setHeaders = useCallback((newHeaders) => {
    _setHeaders(newHeaders);
    _setHasPendingChanges(true);
  }, [_setHeaders, setHasPendingChanges]);

  const setRoles = useCallback((newRoles) => {
    _setRoles(newRoles);
    _setHasPendingChanges(true);
  }, [_setRoles, setHasPendingChanges]);

  const setPluginRefs = useCallback((newPluginRefs) => {
    _setPluginRefs(newPluginRefs);
    _setHasPendingChanges(true);
    form.setFieldsValue({
      loginSettings: {
        pluginRefs: newPluginRefs
      }
    });
    form.validateFields();
  }, [_setPluginRefs, setHasPendingChanges]);

  useEffect(() => {
    if(form) {
      setFormRef(form);
    }

    return () => {
      setFormRef(null);
    };
  }, [setFormRef, form]);

  useEffect(() => {
    if (organization) {
      const headers = Object.entries(organization?.loginSettings?.postAuthenticationRequestHeaders || {});
      const roles = Object.entries(organization?.loginSettings?.roleMap || {});
      _setHeaders(headers.map(([key, value]) => ({ key, value })));
      _setRoles(roles.map(([key, value]) => ({ key, value })));

      const _pluginRefs = organization?.loginSettings?.plugins?.map((plugin) => plugin.integration.id);
      _setPluginRefs(_pluginRefs || []);
      setIsSkipNativeLoginEnabled(organization?.loginSettings?.isSkipNativeLoginEnabled);
    }
  }, [organization]);

  const getPreviewData = useCallback(() => {
    const formValues = form.getFieldsValue();
    return {
      ...organization,
      ...formValues,
      loginSettings: {
        ...organization?.loginSettings,
        ...formValues.loginSettings,
        plugins: pluginRefs.map((id) => orgIntegrations?.items?.find((integ) => integ.id === id)),
        postAuthenticationRequestHeaders: headers.reduce((acc, curr) => ({ ...acc, [curr.key]: curr.value }), {}),
        roleMap: roles.reduce((acc, curr) => ({ ...acc, [curr.key]: curr.value }), {})
      }
    };
  }, [organization, form, pluginRefs, orgIntegrations, headers, roles]);

  const onSubmit = async (values) => {
    const valuesToSubmit = {
      ...values,
      loginSettings: {
        ...values.loginSettings,
        postAuthenticationRequestHeaders: headers.reduce((acc, curr) => ({ ...acc, [curr.key]: curr.value }), {}),
        roleMap: roles.reduce((acc, curr) => ({ ...acc, [curr.key]: curr.value }), {})
      }
    };
    valuesToSubmit.id = organization.id;

    await updateOrganization({ orgId, data: valuesToSubmit }).unwrap();
    setHasPendingChanges(false);
  };

  const handlePreviewClick = () => {
    setIsPreviewModalVisible(true);
  };

  const handlePreviewModalClose = () => {
    setIsPreviewModalVisible(false);
  };

  const handleAddHeader = () => {
    setHeaders([...headers, { key: '', value: '' }]);
  };

  const handleRemoveHeader = (index) => {
    const newHeaders = headers.filter((_, idx) => idx !== index);
    setHeaders(newHeaders);
  };

  const handleHeaderChange = (index, field, value) => {
    const newHeaders = headers.map((header, idx) => (idx === index ? { ...header, [field]: value } : header));
    setHeaders(newHeaders);
  };

  const handleAddRole = () => {
    setRoles([...roles, { key: '', value: '' }]);
  };

  const handleRemoveRole = (index) => {
    const newRoles = roles.filter((_, idx) => idx !== index);
    setRoles(newRoles);
  };

  const handleRoleChange = (index, field, value) => {
    const newRoles = roles.map((role, idx) => (idx === index ? { ...role, [field]: value } : role));
    setRoles(newRoles);
  };

  const validateLoginConfiguration = ({ getFieldValue }) => ({
    validator(_, __) {
      const isGoogleLoginVisible = getFieldValue(['loginSettings', 'isGoogleLoginVisible']);
      const isMicrosoftLoginVisible = getFieldValue(['loginSettings', 'isMicrosoftLoginVisible']);
      const isCredentialLoginVisible = getFieldValue(['loginSettings', 'isCredentialLoginVisible']);
      if (!isCredentialLoginVisible && !isGoogleLoginVisible && !isMicrosoftLoginVisible && pluginRefs.length === 0) {
        return Promise.reject(t('An alternative sign-in method is required'));
      }
      return Promise.resolve();
    }
  });

  const renderContent = () => {
    if (!organization && isLoadingOrganization) {
      return (
        <Card>
          <Row justify="center">
            <Col>
              <Spin />
            </Col>
          </Row>
        </Card>
      );
    }

    if (organization) {
      return (
        <div>
          <FormCard
            name="organizationSettingsForm"
            layout="vertical"
            labelCol={{ xs: 24, sm: 24, md: 10, lg: 10, xl: 8, xxl: 6 }}
            wrapperCol={{ xs: 24, sm: 24, md: 18, lg: 18, xl: 14, xxl: 12 }}
            onFinish={onSubmit}
            form={form}
            initialValues={organization}
            onValuesChange={(_, allFields) => {
              if (allFields.loginSettings.isSkipNativeLoginEnabled != null) {
                setIsSkipNativeLoginEnabled(allFields.loginSettings.isSkipNativeLoginEnabled);
              }
              _setHasPendingChanges(true);
            }}
            onFieldsChange={(_, allFields) => {
              updateState(allFields);
            }}
            footerActions={[
              (isUpdateMode && hasEditPermissions) && (
                <Button
                  key="submit"
                  type="primary"
                  size="large"
                  disabled={isLoadingOrganization || isFetchingMediaFiles}
                  loading={isUpdatingOrganization}
                  onClick={() => {
                    form.submit();
                    setHasPendingChanges(false);
                  }}
                >
                  {t('Save Changes')}
                </Button>
              )
            ]}
          >
            <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
              <Col xs={formProps.mainColSpan}>
                <Typography.Text className="fs-base">
                  {t('Login Experience')}
                </Typography.Text>
                <Typography.Paragraph className="fs-sm" type="secondary">
                  {t('Customize login page for your Workspace users.')}
                </Typography.Paragraph>

                <Button
                  type={'link'}
                  size={'small'}
                  className={'mx-0 px-0'}
                  onClick={() => {
                    handlePreviewClick();
                  }}
                >
                  {t('Preview Example')}
                </Button>
              </Col>

              <Col xs={formProps.secondaryColSpan}>
                <Form.Item
                  name={['loginSettings', 'isOrganizationNameVisible']}
                  valuePropName="checked"
                  className={'mb-0'}
                >
                  <Checkbox disabled={!hasEditPermissions}>{t('Show Workspace Name')}</Checkbox>
                </Form.Item>

                <Form.Item
                  name={['loginSettings', 'isOrganizationLogoVisible']}
                  valuePropName="checked"
                  className={'mb-0'}
                >
                  <Checkbox disabled={!hasEditPermissions}>{t('Show Workspace Logo')}</Checkbox>
                </Form.Item>

                <Form.Item
                  name={['loginSettings', 'isCustomMessageVisible']}
                  valuePropName="checked"
                  className={'mb-0'}
                >
                  <Checkbox disabled={!hasEditPermissions}>{t('Show Custom Message')}</Checkbox>
                </Form.Item>

                <Form.Item
                  name={['loginSettings', 'customMessage']}
                >
                  <Input disabled={!hasEditPermissions} className={'ml-4'} size="large" placeholder={'type custom message'} />
                </Form.Item>

              </Col>
            </Row>

            <Divider className="mt-2" />

            <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
              <Col xs={formProps.mainColSpan}>
                <Typography.Text className="fs-base">
                  {t('Login SSO')}
                </Typography.Text>
                <Typography.Paragraph className="fs-sm" type="secondary">
                  {t('Include one or more SSO Authentications.')}
                </Typography.Paragraph>
              </Col>

              <Col xs={formProps.secondaryColSpan}>
                <Form.Item
                  name={['loginSettings', 'isGoogleLoginVisible']}
                  valuePropName="checked"
                  className={'mb-0'}
                >
                  <Checkbox disabled={!hasEditPermissions}>{t('Sign in with Google')}</Checkbox>
                </Form.Item>

                <Form.Item
                  name={['loginSettings', 'isMicrosoftLoginVisible']}
                  valuePropName="checked"
                  className={'mb-0'}
                >
                  <Checkbox disabled={!hasEditPermissions}>{t('Sign in with Microsoft')}</Checkbox>
                </Form.Item>

                <Form.Item
                  name={['loginSettings', 'isCredentialLoginVisible']}
                  valuePropName="checked"
                  className={'mb-0'}
                  dependencies={[['loginSettings', 'isGoogleLoginVisible'], ['loginSettings', 'isMicrosoftLoginVisible'], ['loginSettings', 'pluginRefs']]}
                  getValueProps={(value) => ({ checked: !value })}
                  getValueFromEvent={(e) => !e.target.checked}
                  rules={[validateLoginConfiguration]}
                  validateTrigger={['onChange', 'onBlur']}
                >
                  <Checkbox disabled={!hasEditPermissions}>
                    <Trans i18nKey="Hide: Sign in with email & password">
                              Hide: <i>Sign in with email & password</i>
                    </Trans>
                  </Checkbox>
                </Form.Item>

                <Typography.Text>{t('Custom SSO:')}</Typography.Text>

                <div className={'ml-5'}>

                  <Form.Item className="mb-0" name={['loginSettings', 'pluginRefs']}>
                    {pluginRefs?.map((pluginRef) => {
                      const _integration = orgIntegrations?.items?.find((integration) => integration.id === pluginRef);
                      return (
                        <Form.Item key={pluginRef} className={'mb-0'}>
                          <p>
                            {t(INTEGRATION_TYPE.OIDC_AUTHENTICATION, { context: 'type' })} -

                            {hasEditPermissions ? (
                              <>
                                <Button
                                  type={'link'}
                                  className={'pl-0 ml-1'}
                                  onClick={() => setSelectedIntegration(_integration)}
                                >
                                  {_integration?.name}
                                </Button>
                                <Button
                                  type={'link'}
                                  icon={<MinusCircleOutlined />}
                                  onClick={() => setPluginRefs(pluginRefs.filter((id) => id !== pluginRef))}
                                />
                              </>
                            ) : ` ${_integration?.name}`}
                          </p>
                        </Form.Item>
                      );
                    })}
                  </Form.Item>

                  {
                    hasEditPermissions ? (
                      <Dropdown
                        menu={{
                          items:
                            [
                              ...orgIntegrations?.items?.map((integration) => {
                                return {
                                  key: integration.id,
                                  disabled: pluginRefs.includes(integration.id),
                                  label: `${t(INTEGRATION_TYPE.OIDC_AUTHENTICATION, { context: 'type' })} - ${integration.name}`
                                };
                              }) || [],
                              {
                                key: 'create-new',
                                label: (
                                  <Button
                                    type="link"
                                    className="m-0 px-0"
                                    icon={<PlusIcon />}
                                    loading={isLoadingOrgIntegrations}
                                    disabled={isLoadingOrgIntegrations}
                                  >
                                    {t('Add New Integration')}
                                  </Button>
                                )
                              }
                            ],
                          onClick: (item) => {
                            if (item.key === 'create-new') {
                              setSelectedIntegration({ type: INTEGRATION_TYPE.OIDC_AUTHENTICATION, category: INTEGRATION_CATEGORY.AUTHENTICATION });
                            } else if (!pluginRefs.includes(item.key)) {
                              setPluginRefs([...pluginRefs, item.key]);
                            }
                          }
                        }}
                      >
                        <Button
                          type="dashed"
                          size="large"
                          className="text-secondary mt-3"
                          icon={<PlusIcon />}
                          loading={isLoadingOrgIntegrations}
                          disabled={isLoadingOrgIntegrations}
                        >
                          {t('Add SSO')}
                        </Button>
                      </Dropdown>
                    ) : null
                  }

                  {
                    pluginRefs.length > 0 ? (
                      <Fragment>
                        <Divider className="mt-4 mb-2" orientation="left" >
                          <Typography.Text className="title text-muted fs-sm">
                            {t('SSO additional settings')}
                          </Typography.Text>
                        </Divider>

                        <Form.Item
                          name={['loginSettings', 'isSkipNativeLoginEnabled']}
                          valuePropName="checked"
                          className={'mb-0'}
                        >
                          <Checkbox disabled={pluginRefs.length > 1 || !hasEditPermissions}>
                            {t('Skip Helvia login page')}
                            <Tooltip title={t('If there is enabled only one custom SSO, this option can be used in order to skip the native login page and redirect to the first available SSO login page.')}>
                              &nbsp;<QuestionCircleOutlined />
                            </Tooltip>
                          </Checkbox>
                        </Form.Item>

                        <Row align="middle" wrap={false} className="mb-2">
                          <Col flex="0 1 464px" className="bg-layout border-radius-base ml-5 px-4 py-3">
                            {t('If authentication fails, allow the redirection of login page:')}
                            <Form.Item
                              name={['loginSettings', 'failedAuthenticationRedirectUrl']}
                              className={'mb-0 mt-1'}
                              wrapperCol={{ span: 24 }}
                            >
                              <Input
                                disabled={pluginRefs.length > 1 || !hasEditPermissions || !isSkipNativeLoginEnabled}
                                size="large"
                                placeholder={'enter redirection url'}
                              />
                            </Form.Item>
                          </Col>
                        </Row>

                        <Form.Item
                          name={['loginSettings', 'isAutoUserCreationEnabled']}
                          valuePropName="checked"
                          className={'mb-0'}
                        >
                          <Checkbox disabled={!hasEditPermissions}>{t('Create user on first login')}</Checkbox>
                        </Form.Item>

                        <Row align="start" wrap={false} className="mb-2">
                          <Col flex="0 1 464px" className="bg-layout border-radius-base ml-5 px-4 py-3">
                            <Typography.Text>
                              {t('Set default user role')}
                              <Tooltip title={`Assign a default role to new users automatically when they log in for the first time via SSO.`}>
                                <QuestionCircleOutlined className="ml-1 mt-1"/>
                              </Tooltip>
                            </Typography.Text>
                            <Form.Item
                              name={['loginSettings', 'defaultAutoCreationRole']}
                              className={'mb-0 w-100'}
                              wrapperCol={{ span: 24 }}
                            >
                              <Select
                                size="large"
                                className="w-100"
                                placeholder={t('ex. viewer')}
                                disabled={!hasEditPermissions}
                                options={Object.keys(PERMISSIONS_ORG).map((key) => {
                                  const labelMap = {
                                    [PERMISSIONS_ORG.VIEWER]: t('Workspace viewer'),
                                    [PERMISSIONS_ORG.EDITOR]: t('Workspace editor'),
                                    [PERMISSIONS_ORG.ADMIN]: t('Workspace admin')
                                  };
                                  return {
                                    value: PERMISSIONS_ORG[key],
                                    label: labelMap[PERMISSIONS_ORG[key]]
                                  };
                                })}
                              />
                            </Form.Item>
                            <Typography.Paragraph className={'fs-sm text-muted mb-0'}>
                              <QuestionCircleOutlined className="text-warning" />&nbsp;
                              <strong>{t('May be overridden by user role mapping.')}</strong>
                            </Typography.Paragraph>
                          </Col>
                        </Row>
                      </Fragment>
                    ) : null
                  }
                </div>

                <Form.Item
                  name={['loginSettings', 'suppressWelcomeEmail']}
                  valuePropName="checked"
                  getValueProps={(value) => ({ checked: !value })}
                  getValueFromEvent={(e) => !e.target.checked}
                >
                  <Checkbox disabled={!hasEditPermissions}>{t('Send welcome email after user\'s creation')}</Checkbox>
                </Form.Item>
              </Col>
            </Row>

            <Divider className="mt-2" />

            <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
              <Col xs={formProps.mainColSpan}>
                <Typography.Text className="fs-base">
                  {t('Login URL')}
                </Typography.Text>
                <Typography.Paragraph className="fs-sm" type="secondary">
                  {t('Use custom login URL for Workspace users.')}
                </Typography.Paragraph>
              </Col>

              <Col xs={formProps.secondaryColSpan}>

                <Form.Item
                  label={t('Workspace User\'s Login URL')}
                >
                  <Input
                    size="large"
                    disabled
                    value={`${window.location.origin}/login?orgId=${organization?.id}`}
                    suffix={
                      <Button
                        type="link"
                        icon={<CopyOutlined />}
                        onClick={() => {
                          copyTextToClipboard(`${window.location.origin}/login?orgId=${organization?.id}`);
                        }}
                      />
                    }
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider className="mt-2" />

            <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
              <Col xs={formProps.mainColSpan}>
                <Typography.Text className="fs-base">
                  {t('Post Authentication URL')}
                </Typography.Text>
                <Typography.Paragraph className="fs-sm" type="secondary">
                  {t('Enable this option to bypass Token with a request to an endpoint that provides user information which later maps the user role in the mapping section.')}
                </Typography.Paragraph>
                <Typography.Paragraph className="fs-sm" type="secondary">
                  <QuestionCircleOutlined className="text-warning" />&nbsp;
                  {t('If enabled will override the "Create user on first login" option.')}
                </Typography.Paragraph>
              </Col>

              <Col xs={formProps.secondaryColSpan}>
                <Form.Item
                  name={['loginSettings', 'isPostAuthenticationEnabled']}
                  label={t('Enable')}
                  valuePropName="checked"
                  className={'mb-2'}
                >
                  <Switch disabled={!hasEditPermissions} />
                </Form.Item>

                <Form.Item
                  label={t('URL')}
                  name={['loginSettings', 'postAuthenticationRequestUrl']}
                  className={'mb-2'}
                >
                  <Input disabled={!hasEditPermissions} size="large" placeholder={'enter url'} />
                </Form.Item>

                <div>
                  <Row gutter={16} className={'mb-2'} >
                    <Col span={(formProps.secondaryColSpan - 2) / 2}>
                      <Typography.Text className="fs-base">
                        {t('Header Name')}
                      </Typography.Text>
                    </Col>
                    <Col span={(formProps.secondaryColSpan - 2) / 2}>
                      <Typography.Text className="fs-base">
                        {t('Header Value')}
                      </Typography.Text>
                    </Col>
                  </Row>

                  {
                    headers.map((header, index) => (
                      <Row key={index} gutter={16} className={'mb-2'}>
                        <Col span={(formProps.secondaryColSpan - 2) / 2}>
                          <Input
                            value={header.key}
                            onChange={(e) => handleHeaderChange(index, 'key', e.target.value)}
                            placeholder={t('Header name')}
                            disabled={!hasEditPermissions}
                          />
                        </Col>
                        <Col span={(formProps.secondaryColSpan - 2) / 2}>
                          <Input
                            value={header.value}
                            onChange={(e) => handleHeaderChange(index, 'value', e.target.value)}
                            placeholder={t('Header value')}
                            disabled={!hasEditPermissions}
                          />
                        </Col>
                        <Col span={2} className="d-flex align-items-center justify-content-center" >
                          {hasEditPermissions ? <MinusCircleOutlined onClick={() => handleRemoveHeader(index)} size={30} /> : null}
                        </Col>
                      </Row>
                    ))
                  }

                  {
                    hasEditPermissions ? (
                      <Button
                        type="dashed"
                        size="large"
                        onClick={handleAddHeader}
                        className="text-secondary mt-3"
                        icon={<PlusIcon />}
                      >
                        {t('Add Header')}
                      </Button>
                    ) : null
                  }
                </div>
              </Col>
            </Row>

            <Fragment>
              <Collapse defaultActiveKey={['1']} ghost>
                <Collapse.Panel
                  showArrow={false}
                  header={
                    <Divider className="fs-base ant-divider-primary" orientation="left">
                      <Typography.Text>
                        <span className="title text-muted">
                          {
                            t('MAPPING')
                          }
                        </span>
                        <span className="ml-3 icon">
                          <ArrowDownIcon className="fs-base text-muted" />
                        </span>
                      </Typography.Text>
                    </Divider>
                  }
                  key="1"
                >
                  <Row gutter={formProps.rowGutter} className="mt-5">
                    <Col xs={formProps.mainColSpan}>
                      <Typography.Text className="fs-base">
                        {t('User Role Mapping')}
                      </Typography.Text>
                      <br />
                      <Typography.Text className="fs-sm">
                        {t('Post Authentication Response')}
                      </Typography.Text>
                      <Typography.Paragraph className="fs-sm" type="secondary">
                        {t('Allows mapping a value from a specified key to a user role.')}
                      </Typography.Paragraph>
                    </Col>

                    <Col span={formProps.secondaryColSpan}>
                      <div>
                        {
                          roles.map((role, index) => (
                            <Row key={index} gutter={16} className={'mb-3'}>
                              <Col span={formProps.secondaryColSpan - 2}>
                                <div className="input-connector pl-0">
                                  <Typography.Text className="fs-base pl-4">
                                    {t('Matching Response Value')}
                                  </Typography.Text>

                                  <div className={'d-flex align-items-center mb-2'}>
                                    <span className={'ich'} />
                                    <span className={'icc'} />
                                    <Input
                                      value={role.key}
                                      onChange={(e) => handleRoleChange(index, 'key', e.target.value)}
                                      placeholder={t('value')}
                                      className={'mb-0'}
                                      disabled={!hasEditPermissions}
                                    />
                                  </div>

                                  <Typography.Text className="fs-base pl-4">
                                    {t('Workspace\'s User Role')}
                                  </Typography.Text>

                                  <div className={'d-flex align-items-center mb-2'}>
                                    <span className={'ich'} />
                                    <span className={'icc'} />
                                    <Select
                                      size="large"
                                      className="w-100"
                                      placeholder={t('ex. viewer')}
                                      value={role.value}
                                      disabled={!hasEditPermissions}
                                      onChange={(value) => handleRoleChange(index, 'value', value)}
                                      options={Object.keys(PERMISSIONS_ORG).map((key) => {
                                        const labelMap = {
                                          [PERMISSIONS_ORG.VIEWER]: t('Workspace viewer'),
                                          [PERMISSIONS_ORG.EDITOR]: t('Workspace editor'),
                                          [PERMISSIONS_ORG.ADMIN]: t('Workspace admin')
                                        };
                                        return {
                                          value: PERMISSIONS_ORG[key],
                                          label: labelMap[PERMISSIONS_ORG[key]]
                                        };
                                      })}
                                    />
                                  </div>
                                </div>
                              </Col>
                              <Col span={2} className="d-flex align-items-center justify-content-center pt-4">
                                {hasEditPermissions ? <MinusCircleOutlined onClick={() => handleRemoveRole(index)} size={30} /> : null}
                              </Col>
                            </Row>
                          ))
                        }

                        {
                          hasEditPermissions ? (
                            <Button
                              type="dashed"
                              size="large"
                              onClick={handleAddRole}
                              className="text-secondary mt-3"
                              icon={<PlusIcon />}
                            >
                              {t('Add Value - Role pair')}
                            </Button>
                          ) : null
                        }
                      </div>
                    </Col>
                  </Row>
                </Collapse.Panel>
              </Collapse>

            </Fragment>

            <Modal
              visible={isPreviewModalVisible}
              onCancel={handlePreviewModalClose}
              footer={null}
              width={1000}
              destroyOnClose
            >
              {/* <img src={SignInPreviewImage} style={{ maxWidth: '100%' }} alt="Sign In Preview" /> */}
              <LoginScreen injectedOrganizationData={getPreviewData()} />
              <div className="position-absolute top-0 right-0 left-0 bottom-0" />
            </Modal>
          </FormCard>

          <SideModal
            open={!!selectedIntegration}
            onClose={() => setSelectedIntegration(null)}
            title={t('Configure Integration')}
            bodyClassName="p-0"
            destroyOnClose
          >
            <IntegrationForm
              integration={selectedIntegration}
              bodyClassName="px-6 pt-6"
              footerClassName="ant-drawer-footer"
              onCancel={() => setSelectedIntegration(null)}
              onSuccess={(data) => {
                if (data?.id) {
                // New integration has been created
                  setPluginRefs([...pluginRefs, data.id]);
                }
                // Close the form
                setSelectedIntegration(null);
              }}
            />
          </SideModal>
        </div>
      );
    }

    if (fetchOrganizationError) {
      return (
        <Empty
          description={
            <Row gutter={16} className="mt-3">
              <Col md={{ span: 12, offset: 6 }}>
                {t('There was an error fetching your data, please refresh your page. If the problem persists, please contact an administrator')}.
              </Col>
            </Row>
          }
          className="mt-3 mb-6"
        />
      );
    }

    // else
    return (
      <Empty
        description={
          <Row gutter={16} className="mt-3">
            <Col md={{ span: 12, offset: 6 }}>
              {t('There are no Workspace Settings available. Please contact an administrator')}.
            </Col>
          </Row>
        }
        className="mt-3 mb-6"
      />
    );
  };

  return renderContent();
};

export default OrganizationLoginSettingsForm;
